/**
 * Cascade Settings Modal — two-step flow for applying a group's settings to its
 * projects and optionally to descendant subgroups.
 *
 * Step 1 ("select"): Toggle switches for which settings to cascade, plus a tree of
 *   descendant groups with two checkboxes each:
 *   - "Update group" — adds the subgroup to subgroup_ids (group model is updated)
 *   - "Include projects" — adds the subgroup to subgroup_ids_for_projects (projects included)
 *   Clicking "Preview Changes" calls the preflight API and transitions to step 2.
 *
 * Step 2 ("preview"): Two-pane layout. Left pane lists selected settings for navigation.
 *   Right pane shows the selected setting's affected entities grouped by current value,
 *   with per-entity and bulk exclude/include controls before the user confirms.
 *
 * Step 3 ("confirm"): Confirmation modal with a summary of the changes and a button to apply the changes.
 *
 * Step 4 ("success"): Success modal with a message and a button to close the modal.
 */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { invalidateCache, loadAllEntities, loadAllReferenceableData } from "../../../cached-data/actions"
import {
    Checkbox,
    colorAttentionBlue50,
    colorAttentionGreen50,
    colorAttentionRed60,
    colorFunctionalGray50,
    colorFunctionalGray70,
    MixedSlideToggle,
    spacingM,
    spacingS,
    spacingXs,
    StandardModal,
} from "@rhumbix/rmbx_design_system_web"
import styled from "styled-components"
import { iCohort, iCompanyGroup, iEmployee } from "../../../cached-data/types"
import { AppDispatch, ReduxState } from "../../../common/types"
import { cohortSelector, employeesSelector } from "../../../selectors/custom-dashboard"
import { companyGroupsSelector } from "../../../selectors"
import { selectRateTablesById } from "../../../rate-tables/selectors"
import { getFlagEnabled } from "../../../getFlagValue"
import * as API from "../../../api"
import { getTaskPollDelayMs } from "../../../common/ts-utils"
import {
    CICO_PARENT_CHILDREN,
    defaultSettings,
    MODAL_WIDTH,
    SETTING_GROUPS,
    SETTING_LABELS,
    TASK_POLL_TIMEOUT_MS,
} from "./constants"
import {
    CascadeSettingKey,
    CascadeSettings,
    CascadeSettingsModalProps,
    EntityType,
    Excluded,
    M2MNameMaps,
    PreflightResult,
} from "./types"
import { buildAllExclusionUpdate, getDescendants, toggleSetMember } from "./utils"
import SubgroupTreeNode, { TreeBody, TreeHeader, TreeHeaderCheckboxCell, TreeHeaderNameCell } from "./SubgroupTree"
import PreviewTwoPaneView, { SettingsPane, TwoPaneContent } from "./PreviewPane"

// ---------------------------------------------------------------------------
// Styled components — shared across steps
// ---------------------------------------------------------------------------

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingM};
    padding-top: ${spacingS};
    padding-bottom: ${spacingS};
    font-family: Roboto, sans-serif;
`

const SectionLabel = styled.p`
    font-size: 12px;
    font-weight: 700;
    color: ${colorFunctionalGray70};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.08em;
`

const IndentedGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingXs};
    padding-left: ${spacingM};
`

const ToggleRow = styled.label`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${spacingS};
    cursor: pointer;
`

const ToggleLabel = styled.span`
    font-size: 13px;
    color: ${colorFunctionalGray70};
`

const ErrorText = styled.p`
    color: ${colorAttentionRed60};
    font-size: 13px;
    margin: 0;
`

const SubgroupPane = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    gap: 0;
`

const NoSubgroupsMessage = styled.p`
    font-size: 13px;
    color: ${colorFunctionalGray50};
    margin: ${spacingM} 0 0;
`

// ---------------------------------------------------------------------------
// Styled components — Step 3 (confirm) and Step 4 (success)
// ---------------------------------------------------------------------------

const ConfirmSummarySection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacingS};
`

const ConfirmSummaryText = styled.p`
    font-size: 14px;
    color: ${colorFunctionalGray70};
    margin: 0;
`

const ConfirmSettingList = styled.ul`
    margin: 0;
    padding-left: ${spacingM};
    display: flex;
    flex-direction: column;
    gap: ${spacingXs};
`

const ConfirmSettingItem = styled.li`
    font-size: 13px;
    color: ${colorFunctionalGray70};
`

const ConfirmWarningBanner = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${colorAttentionRed60};
    margin: 0;
`

const ApplyingStatus = styled.p`
    font-size: 13px;
    color: ${colorFunctionalGray50};
    margin: 0;
    text-align: center;
`

const SuccessMessage = styled.p`
    font-size: 14px;
    color: ${colorFunctionalGray70};
    margin: 0;
    padding: ${spacingM} 0;
`

// ---------------------------------------------------------------------------
// CascadeSettingsModal
// ---------------------------------------------------------------------------

/**
 * Two-step modal for cascading group settings to projects and subgroups.
 *
 * Step 1 selects which settings to cascade and optionally which descendant subgroups
 * to include. Step 2 shows a two-pane preview with a setting nav on the left and
 * the entity list for the active setting on the right, before the user confirms.
 *
 * Parameters:
 * group (iCompanyGroup): The source group.
 * isOpen (boolean): Whether the modal is open.
 * onClose (() => void): Callback to close the modal.
 */
const CascadeSettingsModal: FC<CascadeSettingsModalProps> = ({ group, isOpen, onClose }) => {
    const dispatch = useDispatch() as AppDispatch
    const featureToggles = useSelector((state: ReduxState) => state.featureFlags.features)
    const companyGroups = useSelector(companyGroupsSelector) as Record<number, iCompanyGroup>
    const groupsLoading = useSelector((state: ReduxState) => state.pagination.companyGroups?.all?.isFetching ?? false)

    // Track the last group ID we fetched for so we only invalidate when switching groups,
    // not on every re-open of the same group. We intentionally avoid invalidating on every
    // open because invalidating companyGroups cascades to projects and workRuleSets via
    // entityRelationships, triggering unnecessary re-fetches across the app.
    const lastFetchedGroupIdRef = useRef<number | null>(null)

    const cicoEnabled = featureToggles.cico
    const rateTablesEnabled = featureToggles.rate_tables
    const workRulesEnabled = featureToggles.work_rules

    const rateTablesById = useSelector(selectRateTablesById)
    const workRuleSetsById = useSelector((state: ReduxState) => state.entities?.workRuleSets?.objects ?? {})
    const cohortsById = useSelector(cohortSelector) as Record<number, iCohort>
    const employeesById = useSelector(employeesSelector) as Record<number, iEmployee>

    const ptoEnabled = getFlagEnabled("WA-9197-pto-accruals")
    const cicoOverridesVisible = cicoEnabled && getFlagEnabled("WA-8883-employee-cohort-cico-overrides")
    const workRulesVisible = getFlagEnabled("WA-8985-work-rules") && workRulesEnabled

    // Stable array of employee IDs referenced in CICO overrides. Using sorted string deps so
    // the array reference only changes when the actual IDs change, not on every render.
    const cicoEmployeeIds = useMemo<number[]>(() => {
        const locs = group.cico_settings?.location_employee_overrides ?? []
        const photos = group.cico_settings?.photo_employee_overrides ?? []
        return Array.from(new Set([...locs, ...photos]))
    }, [
        group.cico_settings?.location_employee_overrides?.slice().sort().join(","),
        group.cico_settings?.photo_employee_overrides?.slice().sort().join(","),
    ])

    useEffect(() => {
        if (isOpen) {
            if (group.id != null && lastFetchedGroupIdRef.current !== group.id) {
                dispatch(invalidateCache("companyGroups"))
                lastFetchedGroupIdRef.current = group.id
            }
            dispatch(loadAllEntities("companyGroups"))
        }
    }, [isOpen, dispatch, group.id])

    // Load M2M name lookups when the modal opens so they are ready by the time the user
    // reaches Step 2. Kept in a separate effect so it doesn't affect the companyGroups fetch above.
    // Employees are fetched by specific ID rather than loadAllEntities to avoid loading the full
    // employee list for large companies.
    useEffect(() => {
        if (!isOpen) return

        dispatch(loadAllEntities("cohorts"))

        if (rateTablesEnabled) {
            dispatch(loadAllEntities("rateTables"))
        }
        if (workRulesVisible) {
            dispatch(loadAllEntities("workRuleSets"))
        }
        if (cicoEnabled && cicoEmployeeIds.length > 0) {
            dispatch(loadAllReferenceableData("employees", cicoEmployeeIds))
        }
    }, [isOpen, dispatch, group.id, rateTablesEnabled, workRulesVisible, cicoEnabled, cicoEmployeeIds])

    const nameMaps = useMemo<M2MNameMaps>(
        () => ({
            rateTables: Object.fromEntries(Object.values(rateTablesById).map(rt => [rt.id, rt.name])),
            workRuleSets: Object.fromEntries(Object.values(workRuleSetsById).map(wrs => [wrs.id, wrs.name])),
            cohorts: Object.fromEntries(Object.values(cohortsById).map(c => [c.id, c.name])),
            employees: Object.fromEntries(
                Object.values(employeesById).map(e => [e.id, `${e.first_name} ${e.last_name}`.trim()])
            ),
        }),
        [rateTablesById, workRuleSetsById, cohortsById, employeesById]
    )

    const [step, setStep] = useState<"select" | "preview" | "confirm" | "success">("select")
    const [settings, setSettings] = useState<CascadeSettings>(defaultSettings)
    const [subgroupIds, setSubgroupIds] = useState<Set<number>>(new Set())
    const [subgroupIdsForProjects, setSubgroupIdsForProjects] = useState<Set<number>>(new Set())
    const [preflightResult, setPreflightResult] = useState<PreflightResult | null>(null)
    const [excluded, setExcluded] = useState<Excluded>({})
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [taskId, setTaskId] = useState<string | null>(null)

    const rootChildren = useMemo(() => {
        const childIds = companyGroups[group.id]?.children_ids ?? group.children_ids ?? []
        return childIds
            .map((id: number) => companyGroups[id])
            .filter((g: iCompanyGroup | undefined): g is iCompanyGroup => g != null)
            .sort((a: iCompanyGroup, b: iCompanyGroup) => a.name.localeCompare(b.name))
    }, [group.id, group.children_ids, companyGroups])

    const hasDescendants = rootChildren.length > 0

    const allDescendantIds = useMemo(
        () => getDescendants(rootChildren.map((c: iCompanyGroup) => c.id), companyGroups).map(d => d.group.id),
        [rootChildren, companyGroups]
    )

    const allGroupsChecked = allDescendantIds.length > 0 && allDescendantIds.every((id: number) => subgroupIds.has(id))
    const allProjectsChecked = allDescendantIds.length > 0 && allDescendantIds.every((id: number) => subgroupIdsForProjects.has(id))

    const setSetting = useCallback((key: CascadeSettingKey, value: boolean) => {
        setSettings(prev => ({ ...prev, [key]: value }))
    }, [])

    /**
     * Toggle all CICO sub-settings at once.
     *
     * Parameters:
     * value (boolean): New checked state for all CICO settings.
     */
    const setCicoGroup = useCallback(
        (value: boolean) => {
            setSettings(prev => ({
                ...prev,
                cico_enabled: value,
                cico_rounding: value,
                cico_geofence: value,
                cico_breaks: value,
                cico_meals: value,
                cico_location: value,
                cico_photo: value,
                ...(cicoOverridesVisible && {
                    cico_location_employee_overrides: value,
                    cico_location_cohort_overrides: value,
                    cico_photo_employee_overrides: value,
                    cico_photo_cohort_overrides: value,
                }),
            }))
        },
        [cicoOverridesVisible]
    )

    const cicoGroupChecked =
        settings.cico_enabled &&
        settings.cico_rounding &&
        settings.cico_geofence &&
        settings.cico_breaks &&
        settings.cico_meals &&
        settings.cico_location &&
        settings.cico_photo &&
        (!cicoOverridesVisible ||
            (settings.cico_location_employee_overrides && settings.cico_location_cohort_overrides && settings.cico_photo_employee_overrides && settings.cico_photo_cohort_overrides))

    const toggleSubgroupId = useCallback((id: number, checked: boolean) => {
        setSubgroupIds(prev => toggleSetMember(prev, id, checked))
    }, [])

    const toggleSubgroupForProjects = useCallback((id: number, checked: boolean) => {
        setSubgroupIdsForProjects(prev => toggleSetMember(prev, id, checked))
    }, [])

    const selectAllGroups = useCallback(
        (checked: boolean) => {
            setSubgroupIds(checked ? new Set(allDescendantIds) : new Set())
        },
        [allDescendantIds]
    )

    const selectAllProjects = useCallback(
        (checked: boolean) => {
            setSubgroupIdsForProjects(checked ? new Set(allDescendantIds) : new Set())
        },
        [allDescendantIds]
    )

    const anySettingEnabled = useMemo(() => (Object.values(settings) as boolean[]).some(Boolean), [settings])

    /** Unique entity IDs that will actually be updated after applying per-setting exclusions. */
    const activeEntityCounts = useMemo(() => {
        if (!preflightResult) return { projects: 0, subgroups: 0 }
        const projectIds = new Set<number>()
        const subgroupIdSet = new Set<number>()
        for (const [key, entry] of Object.entries(preflightResult.settings)) {
            const excEntry = excluded[key]
            for (const p of entry.projects) {
                if (!excEntry?.projectIds?.has(p.id)) projectIds.add(p.id)
            }
            for (const s of entry.subgroups) {
                if (!excEntry?.subgroupIds?.has(s.id)) subgroupIdSet.add(s.id)
            }
        }
        return { projects: projectIds.size, subgroups: subgroupIdSet.size }
    }, [preflightResult, excluded])

    /** Setting groups that have at least one entity needing update in the preflight result. */
    const changedSettingGroups = useMemo(
        () => SETTING_GROUPS.filter(g => g.settingKeys.some(k => preflightResult?.settings[k])),
        [preflightResult]
    )

    /**
     * Exclude or restore an entity for a given setting key, cascading to child settings
     * when the setting has CICO parent-child relationships.
     *
     * Parameters:
     * settingKey (CascadeSettingKey): The setting being modified.
     * entityType ("projects" | "subgroups"): Entity type.
     * id (number): Entity ID.
     * exclude (boolean): True to exclude, false to restore.
     */
    const setEntityExclusion = useCallback((settingKey: CascadeSettingKey, entityType: EntityType, id: number, exclude: boolean) => {
        const keysToUpdate = [settingKey, ...(CICO_PARENT_CHILDREN[settingKey] ?? [])]
        setExcluded(prev => {
            const next = { ...prev }
            for (const key of keysToUpdate) {
                const idField: keyof Excluded[string] = entityType === "projects" ? "projectIds" : "subgroupIds"
                const existing = next[key] ?? { projectIds: new Set<number>(), subgroupIds: new Set<number>() }
                next[key] = { ...existing, [idField]: toggleSetMember(existing[idField], id, exclude) }
            }
            return next
        })
    }, [])

    /** Add all entities in the list to the exclusion set for the given setting. */
    const excludeAll = useCallback((settingKey: CascadeSettingKey, entityType: EntityType, entities: Parameters<typeof buildAllExclusionUpdate>[2]) => {
        setExcluded(buildAllExclusionUpdate(settingKey, entityType, entities, true))
    }, [])

    /** Remove all entities in the list from the exclusion set for the given setting. */
    const restoreAll = useCallback((settingKey: CascadeSettingKey, entityType: EntityType, entities: Parameters<typeof buildAllExclusionUpdate>[2]) => {
        setExcluded(buildAllExclusionUpdate(settingKey, entityType, entities, false))
    }, [])

    /** Build the preflight API payload (no exclusions — preview shows everything). */
    const buildPreflightPayload = useCallback(
        () => ({
            ...settings,
            subgroup_ids: [...subgroupIds],
            subgroup_ids_for_projects: [...subgroupIdsForProjects],
        }),
        [settings, subgroupIds, subgroupIdsForProjects]
    )

    /** Build the cascade API payload with current exclusions. */
    const buildCascadePayload = useCallback(() => {
        const excludedForApi: Record<string, { project_ids: number[]; subgroup_ids: number[] }> = {}
        for (const [key, val] of Object.entries(excluded)) {
            if (val.projectIds.size > 0 || val.subgroupIds.size > 0) {
                excludedForApi[key] = {
                    project_ids: [...val.projectIds],
                    subgroup_ids: [...val.subgroupIds],
                }
            }
        }
        return {
            ...settings,
            subgroup_ids: [...subgroupIds],
            subgroup_ids_for_projects: [...subgroupIdsForProjects],
            excluded: excludedForApi,
        }
    }, [settings, subgroupIds, subgroupIdsForProjects, excluded])

    const handleClose = useCallback(() => {
        setStep("select")
        setSettings(defaultSettings())
        setSubgroupIds(new Set())
        setSubgroupIdsForProjects(new Set())
        setPreflightResult(null)
        setExcluded({})
        setError(null)
        setIsLoading(false)
        setTaskId(null)
        onClose()
    }, [onClose])

    /**
     * Poll for the status of a cascade task, using exponential backoff between attempts.
     * Clears taskId and loading state when the task completes or fails.
     */
    useEffect(() => {
        if (!taskId) return
        let cancelled = false
        let attempt = 0
        let timeoutId: ReturnType<typeof setTimeout> | null = null
        const startTime = Date.now()

        const poll = async () => {
            if (cancelled) return
            if (Date.now() - startTime > TASK_POLL_TIMEOUT_MS) {
                setError("Apply timed out. Please refresh, check your settings, and try again.")
                setIsLoading(false)
                setTaskId(null)
                return
            }
            try {
                const result = await API.getCascadeTaskStatus(taskId, group.id) as { status: string; error?: string }
                if (cancelled) return
                if (result.status === "complete") {
                    setStep("success")
                    setIsLoading(false)
                    setTaskId(null)
                } else if (result.status === "failed") {
                    setError(result.error ?? "Failed to apply settings. Please try again.")
                    setIsLoading(false)
                    setTaskId(null)
                } else {
                    attempt++
                    timeoutId = setTimeout(poll, getTaskPollDelayMs(attempt))
                }
            } catch {
                if (!cancelled) {
                    setError("Failed to apply settings. Please try again.")
                    setIsLoading(false)
                    setTaskId(null)
                }
            }
        }

        timeoutId = setTimeout(poll, getTaskPollDelayMs(0))
        return () => {
            cancelled = true
            if (timeoutId != null) clearTimeout(timeoutId)
        }
    }, [taskId])

    const onClickBack = useCallback(() => {
        setStep("select")
        setExcluded({})
        setError(null)
    }, [])

    const onClickPreview = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const result = await API.preflightGroupSettingsCascade(group.id, buildPreflightPayload())
            setPreflightResult(result as PreflightResult)
            setStep("preview")
        } catch {
            setError("Failed to load preview. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }, [group.id, buildPreflightPayload])

    const onClickApply = useCallback(() => {
        setStep("confirm")
        setError(null)
    }, [])

    const onClickConfirm = useCallback(async () => {
        setIsLoading(true)
        setError(null)
        try {
            const result = await API.cascadeGroupSettings(group.id, buildCascadePayload()) as { task_id: string }
            setTaskId(result.task_id)
        } catch {
            setError("Failed to apply settings. Please try again.")
            setIsLoading(false)
        }
    }, [group.id, buildCascadePayload])

    // ── Step 2: Preview ──────────────────────────────────────────────────────

    if (step === "preview" && preflightResult) {
        const { settings: preflightSettings } = preflightResult

        const noChanges = Object.keys(preflightSettings).length === 0

        return (
            <StandardModal
                open={isOpen}
                autoClose={false}
                title="Cascade Settings Preview"
                description={`From ${group.name} to all children.`}
                primaryBtnText={isLoading ? "Applying…" : "Apply Changes"}
                primaryBtnColor={colorAttentionBlue50}
                primaryBtnOnClick={onClickApply}
                primaryBtnDisabled={isLoading || noChanges}
                secondaryBtnText="Back"
                secondaryBtnOnClick={onClickBack}
                secondaryAutoClose={false}
                closeMethod={handleClose}
                width={MODAL_WIDTH}
            >
                <ModalContent data-testid="preview-step">
                    {noChanges ? (
                        <NoSubgroupsMessage>All selected settings already match. No changes will be made.</NoSubgroupsMessage>
                    ) : (
                        <PreviewTwoPaneView
                            group={group}
                            nameMaps={nameMaps}
                            preflightSettings={preflightSettings}
                            excluded={excluded}
                            onExclude={(settingKey, entityType, id) => setEntityExclusion(settingKey, entityType, id, true)}
                            onRestore={(settingKey, entityType, id) => setEntityExclusion(settingKey, entityType, id, false)}
                            onExcludeAll={excludeAll}
                            onRestoreAll={restoreAll}
                        />
                    )}

                    {error && <ErrorText>{error}</ErrorText>}
                </ModalContent>
            </StandardModal>
        )
    }

    // ── Step 3: Confirm ──────────────────────────────────────────────────────

    if (step === "confirm") {
        const subgroupPart = activeEntityCounts.subgroups > 0
            ? `${activeEntityCounts.subgroups} subgroup${activeEntityCounts.subgroups !== 1 ? "s" : ""}`
            : null
        const projectPart = activeEntityCounts.projects > 0
            ? `${activeEntityCounts.projects} project${activeEntityCounts.projects !== 1 ? "s" : ""}`
            : null
        const entityParts = [subgroupPart, projectPart].filter((p): p is string => Boolean(p)).join(" and ")

        return (
            <StandardModal
                open={isOpen}
                autoClose={false}
                title="Are you sure you want to proceed?"
                primaryBtnText={isLoading ? "Applying…" : "Confirm"}
                primaryBtnColor={colorAttentionBlue50}
                primaryBtnOnClick={onClickConfirm}
                primaryBtnDisabled={isLoading}
                secondaryBtnText="Back"
                secondaryBtnOnClick={() => setStep("preview")}
                secondaryBtnDisabled={isLoading}
                secondaryAutoClose={false}
                closeMethod={handleClose}
                width={MODAL_WIDTH}
            >
                <ModalContent>
                    <ConfirmSummaryText>This action cannot be undone.</ConfirmSummaryText>
                    <ConfirmSummarySection>
                        <ConfirmSummaryText>
                            The following settings from <strong>{group.name}</strong> will be applied to{" "}
                            <strong>{entityParts || "the selected entities"}</strong>.
                        </ConfirmSummaryText>
                        <ConfirmSettingList>
                            {changedSettingGroups.map(g => (
                                <ConfirmSettingItem key={g.groupKey}>{g.label}</ConfirmSettingItem>
                            ))}
                        </ConfirmSettingList>
                    </ConfirmSummarySection>
                    <ConfirmWarningBanner>
                        This action will overwrite existing settings unless an exclusion was applied. This cannot be undone. Click Confirm to apply.
                    </ConfirmWarningBanner>
                    {isLoading && <ApplyingStatus>Applying settings, please wait…</ApplyingStatus>}
                    {error && <ErrorText>{error}</ErrorText>}
                </ModalContent>
            </StandardModal>
        )
    }

    // ── Step 4: Success ──────────────────────────────────────────────────────

    if (step === "success") {
        const entityParts = [
            activeEntityCounts.projects > 0 && `${activeEntityCounts.projects} project${activeEntityCounts.projects !== 1 ? "s" : ""}`,
            activeEntityCounts.subgroups > 0 && `${activeEntityCounts.subgroups} subgroup${activeEntityCounts.subgroups !== 1 ? "s" : ""}`,
        ].filter((p): p is string => Boolean(p)).join(" and ")

        return (
            <StandardModal
                open={isOpen}
                autoClose={false}
                title="Settings Applied"
                primaryBtnText="Done"
                primaryBtnColor={colorAttentionGreen50}
                primaryBtnOnClick={handleClose}
                hideSecondaryBtn
                closeMethod={handleClose}
                width={MODAL_WIDTH}
            >
                <ModalContent>
                    <SuccessMessage>
                        Settings from <strong>{group.name}</strong> have been successfully applied
                        {entityParts && <> to <strong>{entityParts}</strong></>}.
                    </SuccessMessage>
                </ModalContent>
            </StandardModal>
        )
    }

    // ── Step 1: Select ───────────────────────────────────────────────────────

    return (
        <StandardModal
            open={isOpen}
            autoClose={false}
            title={`Cascade Settings - ${group.name}`}
            description={`Choose which settings to copy from this group to its ${group?.project_ids?.length ?? 0} projects. This will replace any existing values.`}
            primaryBtnText={isLoading ? "Loading…" : "Preview Changes"}
            primaryBtnColor={colorAttentionBlue50}
            primaryBtnOnClick={onClickPreview}
            primaryBtnDisabled={!anySettingEnabled || isLoading}
            secondaryBtnText="Cancel"
            secondaryBtnOnClick={handleClose}
            closeMethod={handleClose}
            width={MODAL_WIDTH}
        >
            <ModalContent data-testid="select-step">
                <TwoPaneContent>
                    {/* ── Left pane: settings ── */}
                    <SettingsPane>
                        <SectionLabel>Subgroup Only Settings</SectionLabel>

                        <ToggleRow>
                            <ToggleLabel>{SETTING_LABELS.grants_employee_access}</ToggleLabel>
                            <MixedSlideToggle hideLabel checked={settings.grants_employee_access} onChange={v => setSetting("grants_employee_access", v)} />
                        </ToggleRow>
                        <ToggleRow>
                            <ToggleLabel>{SETTING_LABELS.grants_project_access}</ToggleLabel>
                            <MixedSlideToggle hideLabel checked={settings.grants_project_access} onChange={v => setSetting("grants_project_access", v)} />
                        </ToggleRow>

                        <SectionLabel>Settings</SectionLabel>

                        <ToggleRow>
                            <ToggleLabel>{SETTING_LABELS.logo}</ToggleLabel>
                            <MixedSlideToggle hideLabel checked={settings.logo} onChange={v => setSetting("logo", v)} />
                        </ToggleRow>
                        <ToggleRow>
                            <ToggleLabel>{SETTING_LABELS.attestation_language}</ToggleLabel>
                            <MixedSlideToggle hideLabel checked={settings.attestation_language} onChange={v => setSetting("attestation_language", v)} />
                        </ToggleRow>
                        <ToggleRow>
                            <ToggleLabel>{SETTING_LABELS.cohorts}</ToggleLabel>
                            <MixedSlideToggle hideLabel checked={settings.cohorts} onChange={v => setSetting("cohorts", v)} />
                        </ToggleRow>
                        {rateTablesEnabled && (
                            <ToggleRow>
                                <ToggleLabel>{SETTING_LABELS.rate_tables}</ToggleLabel>
                                <MixedSlideToggle hideLabel checked={settings.rate_tables} onChange={v => setSetting("rate_tables", v)} />
                            </ToggleRow>
                        )}
                        {workRulesVisible && (
                            <ToggleRow>
                                <ToggleLabel>{SETTING_LABELS.work_rule_sets}</ToggleLabel>
                                <MixedSlideToggle hideLabel checked={settings.work_rule_sets} onChange={v => setSetting("work_rule_sets", v)} />
                            </ToggleRow>
                        )}
                        {ptoEnabled && (
                            <ToggleRow>
                                <ToggleLabel>{SETTING_LABELS.pto_buckets}</ToggleLabel>
                                <MixedSlideToggle hideLabel checked={settings.pto_buckets} onChange={v => setSetting("pto_buckets", v)} />
                            </ToggleRow>
                        )}

                        {cicoEnabled && (
                            <>
                                <ToggleRow>
                                    <ToggleLabel>Clock-In/Clock-Out (CICO)</ToggleLabel>
                                    <MixedSlideToggle hideLabel checked={cicoGroupChecked} onChange={setCicoGroup} />
                                </ToggleRow>
                                <IndentedGroup>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_enabled}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_enabled} onChange={v => setSetting("cico_enabled", v)} />
                                    </ToggleRow>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_rounding}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_rounding} onChange={v => setSetting("cico_rounding", v)} />
                                    </ToggleRow>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_geofence}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_geofence} onChange={v => setSetting("cico_geofence", v)} />
                                    </ToggleRow>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_breaks}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_breaks} onChange={v => setSetting("cico_breaks", v)} />
                                    </ToggleRow>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_meals}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_meals} onChange={v => setSetting("cico_meals", v)} />
                                    </ToggleRow>
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_location}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_location} onChange={v => setSetting("cico_location", v)} />
                                    </ToggleRow>
                                    {cicoOverridesVisible && settings.cico_location && (
                                        <IndentedGroup>
                                            <ToggleRow>
                                                <ToggleLabel>{SETTING_LABELS.cico_location_employee_overrides}</ToggleLabel>
                                                <MixedSlideToggle hideLabel checked={settings.cico_location_employee_overrides} onChange={v => setSetting("cico_location_employee_overrides", v)} />
                                            </ToggleRow>
                                            <ToggleRow>
                                                <ToggleLabel>{SETTING_LABELS.cico_location_cohort_overrides}</ToggleLabel>
                                                <MixedSlideToggle hideLabel checked={settings.cico_location_cohort_overrides} onChange={v => setSetting("cico_location_cohort_overrides", v)} />
                                            </ToggleRow>
                                        </IndentedGroup>
                                    )}
                                    <ToggleRow>
                                        <ToggleLabel>{SETTING_LABELS.cico_photo}</ToggleLabel>
                                        <MixedSlideToggle hideLabel checked={settings.cico_photo} onChange={v => setSetting("cico_photo", v)} />
                                    </ToggleRow>
                                    {cicoOverridesVisible && settings.cico_photo && (
                                        <IndentedGroup>
                                            <ToggleRow>
                                                <ToggleLabel>{SETTING_LABELS.cico_photo_employee_overrides}</ToggleLabel>
                                                <MixedSlideToggle hideLabel checked={settings.cico_photo_employee_overrides} onChange={v => setSetting("cico_photo_employee_overrides", v)} />
                                            </ToggleRow>
                                            <ToggleRow>
                                                <ToggleLabel>{SETTING_LABELS.cico_photo_cohort_overrides}</ToggleLabel>
                                                <MixedSlideToggle hideLabel checked={settings.cico_photo_cohort_overrides} onChange={v => setSetting("cico_photo_cohort_overrides", v)} />
                                            </ToggleRow>
                                        </IndentedGroup>
                                    )}
                                </IndentedGroup>
                            </>
                        )}
                    </SettingsPane>

                    {/* ── Right pane: subgroup tree ── */}
                    <SubgroupPane>
                        <SectionLabel>Apply to Subgroups</SectionLabel>
                        {groupsLoading ? (
                            <NoSubgroupsMessage>Loading subgroups…</NoSubgroupsMessage>
                        ) : hasDescendants ? (
                            <TreeBody>
                                <TreeHeader>
                                    <TreeHeaderNameCell>Subgroup</TreeHeaderNameCell>
                                    <TreeHeaderCheckboxCell onClick={() => selectAllGroups(!allGroupsChecked)}>
                                        <Checkbox id="select-all-groups" checked={allGroupsChecked} onChange={() => selectAllGroups(!allGroupsChecked)} />
                                        Update
                                        <br />
                                        Group
                                    </TreeHeaderCheckboxCell>
                                    <TreeHeaderCheckboxCell onClick={() => selectAllProjects(!allProjectsChecked)}>
                                        <Checkbox id="select-all-projects" checked={allProjectsChecked} onChange={() => selectAllProjects(!allProjectsChecked)} />
                                        Include projects
                                    </TreeHeaderCheckboxCell>
                                </TreeHeader>
                                {rootChildren.map((child: iCompanyGroup) => (
                                    <SubgroupTreeNode
                                        key={child.id}
                                        group={child}
                                        allGroups={companyGroups}
                                        depth={0}
                                        subgroupIds={subgroupIds}
                                        subgroupIdsForProjects={subgroupIdsForProjects}
                                        onToggleSubgroupId={toggleSubgroupId}
                                        onToggleSubgroupForProjects={toggleSubgroupForProjects}
                                    />
                                ))}
                            </TreeBody>
                        ) : (
                            <NoSubgroupsMessage>This group has no subgroups.</NoSubgroupsMessage>
                        )}
                    </SubgroupPane>
                </TwoPaneContent>
                {error && <ErrorText>{error}</ErrorText>}
            </ModalContent>
        </StandardModal>
    )
}

export type { CascadeSettingsModalProps }
export default CascadeSettingsModal
